import { Box, Typography, Tooltip } from "@mui/material";
import { CustomIcon, Icons } from "components/common/CustomIcon";
import { StepProgress as StepProgressType } from "utils/ingestStepProgress";

interface StepProgressProps {
  steps: StepProgressType[];
  compact?: boolean;
  showNames?: boolean;
  showProgress?: boolean;
}

export const StepProgress = ({
  steps,
  compact = false,
  showNames = false,
  showProgress = false,
}: StepProgressProps) => {
  const calculateProgress = () => {
    const completedSteps = steps.filter(
      (step) => step.status === "completed",
    ).length;
    const totalSteps = steps.length;
    return Math.round((completedSteps / totalSteps) * 100);
  };

  const getStepIcon = (status: StepProgressType["status"]) => {
    switch (status) {
      case "completed":
        return (
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              backgroundColor: "#e8f5e9",
              border: "1px solid #4caf50",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CustomIcon
              name={Icons.CHECKMARK}
              width="12px"
              height="12px"
              fill="#2e7d32"
            />
          </Box>
        );
      case "current":
        return (
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              backgroundColor: "#e3f2fd",
              border: "2px solid #1976d2",
              boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.15)",
            }}
          />
        );
      case "failed":
        return (
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              backgroundColor: "#ffebee",
              border: "1px solid #d32f2f",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CustomIcon
              name={Icons.CLOSE}
              width="12px"
              height="12px"
              fill="#d32f2f"
            />
          </Box>
        );
      case "pending":
      default:
        return (
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              border: "1px solid #d0d0d0",
              backgroundColor: "#f5f5f5",
            }}
          />
        );
    }
  };

  const getStepColor = (status: StepProgressType["status"]) => {
    switch (status) {
      case "completed":
        return "#4caf50";
      case "current":
        return "#1976d2";
      case "failed":
        return "#d32f2f";
      case "pending":
        return "#9e9e9e";
      default:
        return "#9e9e9e";
    }
  };

  if (compact) {
    const progressPercentage = calculateProgress();
    const completedCount = steps.filter(
      (step) => step.status === "completed",
    ).length;
    const totalCount = steps.length;
    const currentStep = steps.find((step) => step.status === "current");
    const failedStep = steps.find((step) => step.status === "failed");

    // Calculate the current step number correctly
    const getCurrentStepNumber = () => {
      if (failedStep) {
        // For failed ingests, show the step number where it failed
        const failedStepIndex = steps.findIndex(
          (step) => step.status === "failed",
        );
        return failedStepIndex + 1;
      } else if (currentStep) {
        // For running ingests, show the step number currently executing
        const currentStepIndex = steps.findIndex(
          (step) => step.status === "current",
        );
        return currentStepIndex + 1;
      }
      return completedCount;
    };

    if (showNames) {
      return (
        <Box>
          {/* Progress Header */}
          {showProgress && progressPercentage < 100 && (
            <Box mb={1}>
              <Typography
                variant="caption"
                sx={{ fontSize: "0.75rem", color: "#666" }}
              >
                {progressPercentage}% complete
              </Typography>
            </Box>
          )}

          {/* Progress Bar */}
          {progressPercentage < 100 && (
            <Box display="flex" alignItems="center" gap={0.5} mb={2}>
              {steps.map((step, index) => (
                <Tooltip
                  key={step.step}
                  title={step.displayName}
                  placement="top"
                >
                  <Box display="flex" alignItems="center">
                    {getStepIcon(step.status)}
                    {index < steps.length - 1 && (
                      <Box
                        sx={{
                          width: 8,
                          height: 1,
                          backgroundColor: "#e0e0e0",
                          mx: 0.5,
                        }}
                      />
                    )}
                  </Box>
                </Tooltip>
              ))}
            </Box>
          )}

          {/* Current/Failed Step */}
          {(currentStep || failedStep) && (
            <Typography
              variant="caption"
              sx={{
                color: failedStep ? getStepColor("failed") : getStepColor("current"),
                fontWeight: 600,
                fontSize: "0.75rem",
                display: "block",
                lineHeight: 1.2,
                mb: 1,
              }}
            >
              {failedStep ? "✗" : "→"} Step {getCurrentStepNumber()} of {totalCount} -{" "}
              {(failedStep || currentStep)?.displayName}
              {!failedStep && currentStep ? "..." : ""}
            </Typography>
          )}

        </Box>
      );
    }

    return (
      <Box>
        {showProgress && progressPercentage < 100 && (
          <Box mb={1}>
            <Typography
              variant="caption"
              sx={{ fontSize: "0.75rem", color: "#666" }}
            >
              {getCurrentStepNumber()}/{totalCount} steps - {progressPercentage}
              % complete
            </Typography>
          </Box>
        )}

        {progressPercentage < 100 && (
          <Box display="flex" alignItems="center" gap={0.5}>
            {steps.map((step, index) => (
              <Tooltip key={step.step} title={step.displayName} placement="top">
                <Box display="flex" alignItems="center">
                  {getStepIcon(step.status)}
                  {index < steps.length - 1 && (
                    <Box
                      sx={{
                        width: 8,
                        height: 1,
                        backgroundColor: "#e0e0e0",
                        mx: 0.5,
                      }}
                    />
                  )}
                </Box>
              </Tooltip>
            ))}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box>
      {steps.map((step, index) => (
        <Box key={step.step} display="flex" alignItems="center" mb={1}>
          <Box display="flex" alignItems="center" minWidth={20}>
            {getStepIcon(step.status)}
            {index < steps.length - 1 && (
              <Box
                sx={{
                  width: 1,
                  height: 16,
                  backgroundColor: "#e0e0e0",
                  ml: 0.75,
                  mt: 1,
                }}
              />
            )}
          </Box>
          <Typography
            variant="body2"
            sx={{
              ml: 1,
              color: getStepColor(step.status),
              fontWeight: step.status === "current" ? 600 : 400,
            }}
          >
            {step.displayName}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};
